import { datadogRum } from "@datadog/browser-rum";
import { useLocalStorage } from "@rehooks/local-storage";
import cx from "classnames";
import { useEffect, useId, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { Cross, ExpandLeft } from "components/icons/generated";
import useLayoutStore from "components/Layout/stores/useLayoutStore";
import DesktopView from "components/Mobile/DesktopView";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import Icon from "ds/components/Icon";
import useSkipFocusButtons from "ds/components/SkipFocusButton/useSkipFocusButtons";
import { AnalyticsPage } from "hooks/useAnalytics";
import useDetectMobile from "hooks/useDetectMobile";
import { ThemeContext } from "views/Theme";
import useTypedContext from "hooks/useTypedContext";

import { FiltersContext } from "..";
import {
  FILTER_DEFAULT_MIN_WIDTH,
  FILTER_EXPANDED_STORAGE_KEY,
  FILTER_WIDTH_STORAGE_KEY,
} from "../constants";
import { createStorageKey } from "../helpers";
import { FilterMode } from "../types";
import SidebarContent from "./SidebarContent";
import SidebarHeadingContent from "./SidebarHeadingContent";
import styles from "./styles.module.css";
import { useSwappableTooltip } from "./useSwappableTooltip";

const HOVER_EFFECT_OPEN_DELAY = 300;
const HOVER_EFFECT_CLOSE_DELAY = 600;

type FiltersSidebarProps = {
  analyticsPage?: AnalyticsPage;
};

const getFiltersTooltipText = (expanded: boolean) => (expanded ? "Hide filters" : "Pin filters");

const FiltersSidebar = ({ analyticsPage }: FiltersSidebarProps) => {
  const isMobile = useDetectMobile();
  const { reducedMotion } = useTypedContext(ThemeContext);
  const { showFilters, setShowFilters } = useLayoutStore(
    useShallow((state) => ({
      showFilters: state.showFilters,
      setShowFilters: state.setShowFilters,
    }))
  );

  const hoverEffectOpenDelay = reducedMotion ? 0 : HOVER_EFFECT_OPEN_DELAY;
  const hoverEffectCloseDelay = reducedMotion ? 0 : HOVER_EFFECT_CLOSE_DELAY;

  const groupId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const { filterType, filtersRevealedOnHover, setFiltersRevealedOnHover } =
    useTypedContext(FiltersContext);

  const [skipButton, backButton] = useSkipFocusButtons();
  const [lastSidebarWidth, setLastSidebarWidth] = useLocalStorage(
    createStorageKey(filterType, FILTER_WIDTH_STORAGE_KEY),
    FILTER_DEFAULT_MIN_WIDTH
  );
  const [filtersExpanded, updateFiltersExpanded] = useLocalStorage(
    createStorageKey(filterType, FILTER_EXPANDED_STORAGE_KEY),
    true
  );

  const [mode, setMode] = useState<FilterMode>(FilterMode.Filters);

  const { tooltipActive, tooltipText, swapText } = useSwappableTooltip({
    initialTooltipText: getFiltersTooltipText(filtersExpanded),
    swapDelay: reducedMotion ? 0 : 300,
  });

  const handleModeChange = (mode: FilterMode) => {
    if (mode === FilterMode.Views) {
      datadogRum.addAction("[Saved Views] Click Views tab");
    }
    setMode(mode);
  };

  const revealSidebarTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const collapseSidebarTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  const clearRevealSidebarTimeout = () => {
    if (revealSidebarTimeout.current) {
      clearTimeout(revealSidebarTimeout.current);
    }
  };

  const clearCollapseSidebarTimeout = () => {
    if (collapseSidebarTimeout.current) {
      clearTimeout(collapseSidebarTimeout.current);
    }
  };

  const handleSidebarMouseEnter = () => {
    clearRevealSidebarTimeout();
    clearCollapseSidebarTimeout();
  };

  const handleSidebarContentMouseEnter = () => {
    clearRevealSidebarTimeout();
    clearCollapseSidebarTimeout();

    if (!filtersExpanded) {
      revealSidebarTimeout.current = setTimeout(() => {
        setFiltersRevealedOnHover(true);
      }, hoverEffectOpenDelay);
    }
  };

  const handleSidebarMouseLeave = () => {
    clearRevealSidebarTimeout();
    clearCollapseSidebarTimeout();

    if (filtersRevealedOnHover) {
      collapseSidebarTimeout.current = setTimeout(() => {
        setFiltersRevealedOnHover(false);
      }, hoverEffectCloseDelay);
    }
  };

  const toggleFilters = () => {
    datadogRum.addAction(`[Filters] ${filtersExpanded ? "Hide" : "Pin"} filters`);

    if (filtersExpanded && containerRef.current) {
      setLastSidebarWidth(containerRef.current.offsetWidth);
    }

    if (filtersExpanded) {
      setFiltersRevealedOnHover(false);
    }
    updateFiltersExpanded(!filtersExpanded);
    swapText(getFiltersTooltipText(!filtersExpanded));
    setShowFilters(!showFilters);
  };

  useEffect(() => {
    if (isMobile) {
      updateFiltersExpanded(showFilters);
    }
  }, [showFilters, updateFiltersExpanded, isMobile]);

  return (
    <Box
      id={groupId}
      direction="column"
      grow="1"
      ref={containerRef}
      style={
        {
          "--filters-sidebar-collapsed-min-width": `${lastSidebarWidth}px`,
        } as React.CSSProperties
      }
      onMouseLeave={handleSidebarMouseLeave}
      onMouseEnter={handleSidebarMouseEnter}
      className={cx(styles.sidebar, {
        [styles.hide]: isMobile ? !showFilters : !filtersExpanded,
        [styles.revealedOnHover]: filtersRevealedOnHover,
      })}
    >
      {skipButton({
        label: "Skip filters",
      })}
      <Box
        direction="row"
        align="center"
        justify="between"
        className={cx(styles.header, { [styles.mobileClosed]: isMobile && !filtersExpanded })}
      >
        <Box
          direction="column"
          grow="1"
          className={cx(styles.contentToHide, styles.sidebarHeadingWrapper)}
        >
          <SidebarHeadingContent setMode={handleModeChange} mode={mode} />
        </Box>
        {/* This is a valid case, we don't want the peek functionality for keyboard anyway */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <span onMouseEnter={clearRevealSidebarTimeout}>
          <DesktopView>
            <ButtonIcon
              className={cx(styles.toggle, {
                [styles.iconRotated]: !filtersExpanded,
              })}
              iconRotate={!filtersExpanded ? "180" : undefined}
              icon={ExpandLeft}
              onPress={toggleFilters}
              disableTooltip={!tooltipActive}
              aria-controls={groupId}
              aria-expanded={filtersExpanded}
              variant="ghost"
            >
              {tooltipText}
            </ButtonIcon>
          </DesktopView>

          <button
            className={cx(styles.toggleMobile, {
              [styles.iconRotated]: !filtersExpanded,
            })}
            onClick={toggleFilters}
            aria-controls={groupId}
            aria-expanded={filtersExpanded}
          >
            <Icon src={Cross} noShrink />
          </button>
        </span>
      </Box>

      <Box direction="column" grow="1" onMouseEnter={handleSidebarContentMouseEnter}>
        <Box direction="column" grow="1" className={styles.contentToHide}>
          <SidebarContent mode={mode} analyticsPage={analyticsPage} />
        </Box>
      </Box>
      {backButton({
        label: "Back to filters",
        className: styles.backToFilters,
      })}
    </Box>
  );
};

export default FiltersSidebar;
